import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import RegisterPage from './RegisterPage'
import SelectQuizPage from './SelectQuizPage'
import QuizPage from './QuizPage'
import ThankYouPage from './ThankYouPage'
import RegisterPageAI from './RegisterPageAI'
import SelectQuizPageAI from './SelectQuizPageAI'
import QuizPageAI from './QuizPageAI'
import ThankYouPageAI from './ThankYouPageAI'
import { PublicQuizAPI } from '../../apis/PublicQuizAPI'

type Step = 'register' | 'select' | 'quiz' | 'done'

interface FlowState {
  quizStep: Step
  attemptId?: string
  isResuming?: boolean
  assessmentName?: string
  questionCap?: number | null
  userName?: string
  userEmail?: string
  userRole?: string
}

export default function QuizFlow() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const aiMode = useMemo(() => new URLSearchParams(location.search).get('ai') === '1', [location.search])

  const Register   = aiMode ? RegisterPageAI   : RegisterPage
  const SelectQuiz = aiMode ? SelectQuizPageAI : SelectQuizPage
  const Quiz       = aiMode ? QuizPageAI       : QuizPage
  const ThankYou   = aiMode ? ThankYouPageAI   : ThankYouPage

  // Validate slug on mount — redirect to /quiz if it doesn't exist in DB
  useEffect(() => {
    if (!slug) return
    PublicQuizAPI.fetchInfo(slug).catch(() => {
      navigate('/digital', { replace: true })
    })
  }, [slug, navigate])

  // All step data lives in location.state so browser back/forward work naturally.
  const flow = ((location.state ?? {}) as Partial<FlowState>)
  const step: Step = flow.quizStep ?? 'register'
  const attemptId = flow.attemptId ?? ''
  const isResuming = flow.isResuming ?? false
  const assessmentName = flow.assessmentName ?? 'Digital capability'
  const questionCap = flow.questionCap ?? null
  const userName = flow.userName ?? ''
  const userEmail = flow.userEmail ?? ''
  const userRole = flow.userRole ?? ''

  // Question count only matters within a single quiz session — no need in history state.
  const [initialQuestionCount, setInitialQuestionCount] = useState(0)

  function goTo(newStep: Step, extra: Partial<FlowState> = {}) {
    navigate(location.pathname + location.search, {
      state: { ...flow, quizStep: newStep, ...extra } as FlowState,
      replace: false,
    })
  }

  const handleStart = (id: string, resuming: boolean, name: string, cap: number | null) => {
    setInitialQuestionCount(0)
    goTo('quiz', { attemptId: id, isResuming: resuming, assessmentName: name, questionCap: cap })
  }

  const handleFormSubmit = (name: string, email: string, role: string) => {
    goTo('select', { userName: name, userEmail: email, userRole: role })
  }

  const handleQuestionCount = (count: number) => {
    setInitialQuestionCount(count)
  }

  const handleComplete = () => {
    goTo('done')
  }

  const handleRestart = () => {
    navigate(location.pathname + location.search, {
      state: { quizStep: 'register', userName, userEmail, userRole } as FlowState,
      replace: true,
    })
  }

  // ── No-slug flow: register → select → quiz → done ─────────────────────────
  if (!slug) {
    if (step === 'register') {
      return <Register onFormSubmit={handleFormSubmit} initialName={userName} initialEmail={userEmail} />
    }
    if (step === 'select') {
      return (
        <SelectQuiz
          name={userName}
          email={userEmail}
          role={userRole}
          onStart={handleStart}
          onBack={() => navigate(-1)}
        />
      )
    }
    if (step === 'quiz') {
      return (
        <Quiz
          attemptId={attemptId}
          isResuming={isResuming}
          assessmentName={assessmentName}
          questionCap={questionCap}
          initialQuestionCount={initialQuestionCount}
          onQuestionCount={handleQuestionCount}
          onComplete={handleComplete}
          onRestart={handleRestart}
          domain={process.env.REACT_APP_REPORT_DOMAIN}
        />
      )
    }
    return <ThankYou onRestart={handleRestart} />
  }

  // ── Slug flow: register → quiz → done ─────────────────────────────────────
  if (step === 'register') return <Register slug={slug} onStart={handleStart} initialName={userName} initialEmail={userEmail} />
  if (step === 'quiz') return (
    <Quiz
      attemptId={attemptId}
      isResuming={isResuming}
      assessmentName={assessmentName}
      questionCap={questionCap}
      initialQuestionCount={initialQuestionCount}
      onQuestionCount={handleQuestionCount}
      onComplete={handleComplete}
      onRestart={handleRestart}
      domain={process.env.REACT_APP_REPORT_DOMAIN}
    />
  )
  return <ThankYou onRestart={handleRestart} />
}


